import { interactionAnchor, Rect as RectModel } from "../shared/src/types/Interaction"

export const mergeAnchors = (anchor1:interactionAnchor, anchor2:interactionAnchor) => {
  return {
    nodeId: anchor1.nodeId,
    relText: anchor1.relText + ' ' + anchor2.relText,
    rects: [...anchor1.rects, ...anchor2.rects],
    tool: anchor1.tool,
  }
}

// determines the minimal distance between the outside of a rectangle and a point; returns zero for points inside the rectangle
export const distanceToRect = (x:number, y:number, rect:DOMRect) => {
  var dx = 0
  var dy = 0
  if (x < rect.x)      dx = (x-rect.x)**2
  if (x > rect.right)  dx = (x-rect.right)**2
  if (y < rect.y)      dy = (y-rect.y)**2
  if (y > rect.bottom) dy = (y-rect.bottom)**2
  return Math.sqrt(dx+dy)
}

export const rectIntersect = (r1:any, r2:any) => {

  const a1x = r1.left,
        a1y = r1.top,
        a2x = r1.left + r1.width,
        a2y = r1.top + r1.height,

        b1x = r2.left,
        b1y = r2.top,
        b2x = r2.left + r2.width,
        b2y = r2.top + r2.height

  const x_overlap = Math.max(0, Math.min(a2x,b2x) - Math.max(a1x,b1x));
  const y_overlap = Math.max(0, Math.min(a2y,b2y) - Math.max(a1y,b1y));

  const intersect_x = Math.max(a1x, b1x);
  const intersect_y = Math.max(a1y, b1y);

  return {
      left:intersect_x,
      top:intersect_y,
      width: x_overlap,
      height: y_overlap,
      squarepix: x_overlap * y_overlap
  }

}

export const getPageFromCoordinate = ( storeId: string, x: number, y: number) => {
  if(storeId && (typeof x !== 'undefined') && (typeof y !== 'undefined')) {
    // get all elements below the active point
    const elements = document.elementsFromPoint(x, y)
    // check whether there is a page element under the elements
    for(const element of elements) {
      // does the movement take place over a page
      if(element.id.includes(`${storeId}-page-`) || element.id.includes(`${storeId}-preview-image-`)) {
        // get actual page element
        const pageElement = element.children[1]
        const pageNumber = element.id.split("-").pop()
        if(pageElement && pageNumber) {
          return {
            pageNumber: parseInt(pageNumber)+1,
            pageElement: pageElement,
            pageRect: pageElement.getBoundingClientRect()
          }
        }
      }
    }
  }
  return null
}


export const getPdfSpans = (element:Element) => {

  const spans:Element[] = []
  var e:Node|null = null

  const walkerFilter = (node:Node) => {
    if ((node.nodeName === 'SPAN') && (node as Element).getAttribute('role') === 'presentation' ) return NodeFilter.FILTER_ACCEPT
    return NodeFilter.FILTER_SKIP
  }
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_ELEMENT, { acceptNode: walkerFilter })
  while((e = walker.nextNode())) {
    if (e) spans.push(e as Element)
  }
  return spans
}

export const getCaretRangeAtXY = (x:number, y:number) => {

  var node:Element|null = null
  var offset:number = 0
  var distance:number|null = null

  if (typeof document.createRange === "undefined") {
    console.error('Cannot use document.createRange. Abort.');
    return null
  }

  const pageElement:Element|null = document.elementsFromPoint(x, y).filter(e => e.getAttribute('class') === 'react-pdf__Page')[0] || null
  if (!pageElement) { console.error(`Missing pageElement`); return null }

  const pageNumber = parseInt(pageElement.getAttribute('data-page-number')||'')
  if (!pageNumber) { console.error(`Missing pageNumber`); return null }

  let pageTextElement = null
  for(const element of pageElement.children) {
    if(element.className.includes("textContent")) {
      pageTextElement = element
    }
  }
  if (!pageTextElement) { console.error(`Missing pageTextElement`); return null }

  // Check all applicable children of the page to find the geometrically closest valid node
  try {

    const spans = getPdfSpans(pageTextElement)
    for (var elm of spans) {
      // skip the ones that do not have a textNode as child
      const span:Node = elm.childNodes[0]
      if ((!span) || (span.nodeType !== 3) || (span.nodeName !== '#text')) continue

      if (elm && elm.getBoundingClientRect) {
        const spanrect = elm.getBoundingClientRect()
        const d = distanceToRect(x, y, spanrect)
        if (distance === null || d<distance) {
          distance = d
          node = elm
        }
      }
    }

    if (!node?.childNodes[0]) { console.error(`Node has no children`); return null }

    // Inside the geometrically closest node, find the character that is geometrically closest to the given point
    distance = null
    if (node && node.textContent && node.textContent.length) for (var i=0; i<node.textContent.length; i++) {
      try {
        const range = document.createRange()
        range.setStart(node.childNodes[0], i)
        range.setEnd(node.childNodes[0], i+1)
        const spanrect = range.getBoundingClientRect()
        const d = distanceToRect(x, y, spanrect)
        if (distance === null || d<distance) {
          distance = d
          offset = i
          if (x > (spanrect.x + spanrect.right)/2) offset = i+1
        }
      } catch (e) {
        console.error(e)
      }
    }

  }
  catch(e) {
    console.error(e)
    return null
  }

  return {
    t0: Date.now(),
    node: node.childNodes[0],
    offset: offset,
    pageNumber: pageNumber,
    clientX: x,
    clientY: y,
  }
}

export function optimizeRects(rects:RectModel[], verbose = false) {

  type RectModel2 = {
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    p: number,
  }

  const nrects:RectModel2[] = rects.map((r:RectModel) => ({
    x1: r.x,
    y1: r.y,
    x2: r.x + r.w,
    y2: r.y + r.h,
    p: r.p
  }))

  if (verbose) console.log(nrects)

  const intersect = (rect1: any, rect2: any) => {
    return Math.max(0, Math.min(rect1.x2, rect2.x2) - Math.max(rect1.x1, rect2.x1)) * Math.max(0, Math.min(rect1.y2, rect2.y2) - Math.max(rect1.y1, rect2.y1))
  }

  const union = (rect1: any, rect2: any) => {
    return ((rect1.x2 - rect1.x1) * (rect1.y2 - rect1.y1)) + ((rect2.x2 - rect2.x1) * (rect2.y2 - rect2.y1)) - intersect(rect1, rect2)
  }

  const calc = (i0: any, i1: any) => {
    const nrect1 = nrects[i0]
    const nrect2 = nrects[i1]
    const si = intersect(nrect1, nrect2 )
    const su = union(nrect1, nrect2)
    if (verbose) console.log(si, su, 100 * si/su )
    return 100 * si/su
  }

  for(var i0=0; i0<nrects.length; i0++) {
    if (nrects[i0].p === -1) continue
    for(var i1=i0+1; i1<nrects.length; i1++) {
      if (nrects[i1].p === -1) continue
      const ratio = calc(i0, i1)
      if (verbose) console.log(`Compare ${i0} to ${i1}: `, ratio)
      if (ratio > 70) {
        if (verbose) console.log('merge')
        nrects[i0] = {
          x1: Math.min(nrects[i0].x1, nrects[i1].x1),
          y1: Math.min(nrects[i0].y1, nrects[i1].y1),
          x2: Math.max(nrects[i0].x2, nrects[i1].x2),
          y2: Math.max(nrects[i0].y2, nrects[i1].y2),
          p: nrects[i0].p
        }
        nrects[i1].p = -1
      }
    }
  }

  const orect:(RectModel|null)[] = nrects.filter((n: any) => n.p !== -1).map((r: any) => ({
    x: r.x1,
    y: r.y1,
    w: r.x2 - r.x1,
    h: r.y2 - r.y1,
    p: r.p
  }))

  const jointRect = (a:RectModel, b:RectModel) => {
    const result:RectModel = ({
      x: Math.min(a.x, b.x),
      y: Math.min(a.y, b.y),
      w: Math.max(a.x + a.w, b.x + b.w) - Math.min(a.x, b.x),
      h: Math.max(a.y + a.h, b.y + b.h) - Math.min(a.y, b.y),
      p: a.p
    })
    return result
  }

  const merger = (array: (RectModel|null)[], i:number) => {
    if (!array[i]) return array[i]
    const area_1 = array[i].w * array[i].h
    const j = orect.findIndex((val, k) => {
      if (k<=i) return false
      if (!array[i]) return array[i]
      if (!array[k]) return false
      if (array[i].p !== array[k].p) return false
      const area_2 = array[k].w * array[k].h
      const joint = jointRect(array[i], array[k])
      const area_joint = joint.w*joint.h
      const areaDifferential = Math.abs((area_1 + area_2) - area_joint)
      if (((areaDifferential / area_joint)<.2) && (Math.abs(array[i].y - joint.y) < 5) && (Math.abs((array[i].y + array[i].h) - (joint.y + joint.h)) < 5)) return true
      return false
    })
    if ((j>i) && (array[j])) {
      array[i] = jointRect(array[i], array[j])
      array[j] = null
    }
  }

  for(var q=0; q<5; q++)
  for(var i=0; i<orect.length; i++) {
    merger(orect, i)
  }

  const prect:RectModel[] = orect.filter(r => r!==null)

  return prect
}